import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from 'react-i18next';
import LazyImage from "@/components/LazyImage";
import {
  ArrowRight,
  Users,
  Award,
  Truck,
  Shield,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import HeroCarousel from "@/components/HeroCarousel";
import { supabase } from "@/lib/supabase";

// Componente para las estrellas de calificación con animación de Uiverse.io
const StarRating = ({
  rating,
  onRatingChange,
  readOnly = false,
  size = "md",
}) => {
  const sizePx = size === 'sm' ? '24' : size === 'md' ? '30' : '35';

  // Función para manejar el cambio de calificación
  const handleRatingChange = (star) => {
    if (onRatingChange) {
      onRatingChange(star);
    }
  };

  return (
    <div className="flex justify-center w-full">
      {[1, 2, 3, 4, 5].map((star) => (
        <div
          key={star}
          className="relative"
          onClick={() => !readOnly && handleRatingChange(star)}
        >
          <svg
            strokeLinejoin="round"
            strokeLinecap="round"
            strokeWidth="2"
            stroke="#ccc"
            fill={star <= rating ? "#AE0404" : "none"}
            viewBox="0 0 24 24"
            height={sizePx}
            width={sizePx}
            className={`${!readOnly ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </div>
      ))}
    </div>
  );
};

// Componente para mostrar el avatar del usuario
const UserAvatar = ({ name, className = "" }) => {
  const initials = name
    ? name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
    : "U";

  return (
    <div
      className={`w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-semibold text-lg ${className}`}
    >
      {initials}
    </div>
  );
};

// Componente para la tarjeta de reseña
const ReviewCard = ({ review }) => {
  const { t, i18n } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  // Formatear la fecha
  const formatDate = (dateString) => {
    if (!dateString) return t('home.dateNotAvailable');

    try {
      const date = new Date(dateString);
      const months = [
        t('months.january'), t('months.february'), t('months.march'),
        t('months.april'), t('months.may'), t('months.june'),
        t('months.july'), t('months.august'), t('months.september'),
        t('months.october'), t('months.november'), t('months.december')
      ];
      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();

      if (i18n.language === 'es') {
        return `${day} de ${month} de ${year}`;
      }
      return `${month} ${day}, ${year}`;
    } catch (e) {
      console.error('Error al formatear la fecha:', e);
      return t('home.invalidDate');
    }
  };



  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{
        y: -5,
        boxShadow: "0 10px 25px -5px rgba(100, 64, 19, 0.3)",
        borderColor: "rgba(100, 64, 19, 0.5)",
      }}
      transition={{ type: "spring", stiffness: 300, damping: 15 }}
      className="h-full bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 hover:border-red-300 transition-all duration-300 flex flex-col"
    >
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-start gap-4">
          <UserAvatar name={review.nombre_cliente} />
          <div className="flex-1 min-w-0">
            <div className="flex flex-col">
              <div className="min-w-0">
                <h4 className="font-semibold text-gray-900 text-base">
                  {review.nombre_cliente || t('home.anonymous')}
                </h4>
                {review.empresa && (
                  <p className="text-sm text-gray-600 mt-1">
                    {review.empresa}
                  </p>
                )}
              </div>
              <div className="mt-2">
                <StarRating
                  rating={review.calificacion}
                  readOnly
                  size="sm"
                  className="scale-95 origin-left"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex-1">
          <div className={`text-gray-700 ${!isExpanded ? "line-clamp-4" : ""} leading-relaxed`}>
            "{i18n.language === 'en' && review.opinion_en ? review.opinion_en : review.opinion}"
          </div>
          {review.opinion && review.opinion.split(" ").length > 15 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-2 text-sm font-medium text-red-600 hover:text-red-700 focus:outline-none transition-colors"
            >
              {isExpanded ? t('home.showLess') : t('home.readMore')}
            </button>
          )}
        </div>

        <div className="mt-4 pt-3 border-t border-gray-100">
          <p className="text-sm text-gray-500">
            {formatDate(review.fecha_creacion)}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Configuración de animación para el efecto de hover
const hoverAnimation = {
  y: -5,
  boxShadow: "0 20px 40px rgba(100, 64, 19, 0.2)",
  transition: {
    type: "spring",
    stiffness: 400,
    damping: 10,
  },
};

const transitionConfig = {
  type: "spring",
  stiffness: 400,
  damping: 10,
};

const Home = () => {
  const { t } = useTranslation();
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formError, setFormError] = useState("");
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [review, setReview] = useState({
    name: "",
    company: "",
    comment: "",
    rating: null, // Start with no rating selected
  });

  // Manejadores de cambios
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReview((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRatingChange = (rating) => {
    setReview((prev) => ({
      ...prev,
      rating,
    }));
  };

  // Cargar opiniones desde Supabase
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setIsLoading(true);

        // Consulta simple para obtener todas las opiniones
        const { data, error } = await supabase.from("opiniones").select("*");

        if (error) throw error;

        // Verificar la estructura de los datos
        if (data && data.length > 0) {

          // Mapear los datos al formato esperado
          const formattedReviews = data.map((review) => ({
            ...review,
            // Asegurarse de que existan los campos requeridos
            nombre_cliente: review.nombre_cliente || "Anónimo",
            empresa: review.empresa || "",
            opinion: review.opinion || "",
            calificacion: review.calificacion || 5,
            // Usar la fecha disponible o la actual
            fecha_creacion:
              review.fecha_creacion ||
              review.created_at ||
              new Date().toISOString(),
          }));

          setReviews(formattedReviews);
        } else {
          setReviews([]);
        }
      } catch (err) {
        console.error("Error al cargar las opiniones:", err);
        setError(t('home.reviewsLoadError'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchReviews();
  }, [t]);

  // Enviar reseña

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    // Validación básica
    if (!review.comment.trim()) {
      setFormError("Por favor escribe tu opinión");
      return;
    }

    if (!review.company.trim()) {
      setFormError("Por favor proporciona el nombre de tu empresa");
      return;
    }

    if (!review.rating || review.rating < 1 || review.rating > 5) {
      setFormError("Por favor selecciona una calificación");
      return;
    }

    setFormError("");
    setIsSubmitting(true);

    try {
      // Insertar en la tabla 'opiniones'
      const { data, error } = await supabase
        .from("opiniones")
        .insert([
          {
            nombre_cliente: review.name || "Anónimo",
            empresa: review.company || null,
            opinion: review.comment,
            calificacion: review.rating,
          },
        ])
        .select();

      if (error) throw error;

      // Actualizar la lista de opiniones con la nueva
      if (data && data[0]) {
        const newReview = {
          ...data[0],
          // Asegurarse de tener los campos necesarios para el componente ReviewCard
          fecha_creacion: new Date().toISOString(),
          id: data[0].id || Date.now().toString(),
        };
        setReviews((prevReviews) => [newReview, ...prevReviews]);
      }

      // Éxito
      setSubmitSuccess(true);
      setReview({
        name: "",
        company: "",
        comment: "",
        rating: 5,
      });

      // Ocultar el mensaje de éxito después de 3 segundos
      setTimeout(() => {
        setSubmitSuccess(false);
        setShowReviewForm(false);
      }, 3000);
    } catch (error) {
      console.error("Error al enviar la reseña:", error);
      setFormError(
        "Ocurrió un error al enviar tu reseña. Por favor, inténtalo de nuevo."
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  const features = [
    {
      icon: Users,
      title: t('home.feature1.title'),
      description: t('home.feature1.description'),
    },
    {
      icon: Award,
      title: t('home.feature2.title'),
      description: t('home.feature2.description'),
    },
    {
      icon: Truck,
      title: t('home.feature3.title'),
      description: t('home.feature3.description'),
    },
    {
      icon: Shield,
      title: t('home.feature4.title'),
      description: t('home.feature4.description')
    }
  ];
  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Hero Section with Carousel */}
      <section className="relative w-full h-[50vh] sm:h-[60vh] md:h-[70vh] lg:h-[80vh] overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <HeroCarousel />
        </div>

        {/* Efecto de brillo simple - solo en el carrusel */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-[100]">
          <motion.div
            className="absolute w-full h-[1px] bg-gradient-to-r from-transparent via-white/30 to-transparent"
            style={{ top: '90px' }}
            animate={{ left: ["-100%", "100%"] }}
            transition={{
              repeat: Infinity,
              duration: 12,
              ease: "linear",

            }}
          />
          <motion.div
            className="absolute w-full h-[1px] bg-gradient-to-r from-transparent via-red-500/30 to-transparent"
            style={{ top: '90px' }}
            animate={{ left: ["-100%", "100%"] }}
            transition={{
              repeat: Infinity,
              duration: 12,
              ease: "linear",
              delay: 6
            }}
          />
        </div>

        {/* Welcome Banner */}
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/90 to-transparent py-4 z-20">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
                className="mb-4 md:mb-0"
              >
                <motion.h1
                  className="text-2xl font-bold text-white overflow-hidden"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  {t('home.welcomeTitle')} <motion.span
                    className="text-red-500 inline-block"
                    initial={{ scale: 0.7, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                  >PLASA</motion.span>!
                </motion.h1>
                <motion.p
                  className="text-gray-300 relative"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.8 }}
                >
                  {Array.from(t('home.welcomeSubtitle')).map((char, index) => (
                    <motion.span
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.8 + index * 0.02 }}
                    >
                      {char}
                    </motion.span>
                  ))}
                </motion.p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="flex flex-col items-start space-y-2 md:flex-row md:space-y-0 md:space-x-2 mt-5 md:mt-0"
              >
                <Link to="/producto/dollaro">
                  <motion.button
                    className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white py-2 px-4 rounded-lg text-sm font-medium transition-all duration-300 flex items-center border border-white/10 relative overflow-hidden"
                    whileHover={{ scale: 1.05, borderColor: "rgba(239, 68, 68, 0.5)" }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <motion.div
                      className="absolute inset-0 w-full bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"
                      animate={{ x: ["-100%", "100%"] }}
                      transition={{
                        repeat: Infinity,
                        duration: .8,
                        ease: "linear",
                        repeatDelay: 3
                      }}
                    />
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2 z-10 relative"></span>
                    <span className="z-10 relative">{t('home.dollaroNappaBtn')}</span>
                  </motion.button>
                </Link>
                <Link to="/automotriz">
                  <motion.button
                    className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white py-2 px-4 rounded-lg text-sm font-medium transition-all duration-300 flex items-center border border-white/10 relative overflow-hidden"
                    whileHover={{ scale: 1.05, borderColor: "rgba(239, 68, 68, 0.5)" }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <motion.div
                      className="absolute inset-0 w-full bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"
                      animate={{ x: ["-100%", "100%"] }}
                      transition={{
                        repeat: Infinity,
                        duration: .8,
                        ease: "linear",
                        delay: .35,
                        repeatDelay: 3
                      }}
                    />
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2 z-10 relative"></span>
                    <span className="z-10 relative">{t('home.automotiveBtn')}</span>
                  </motion.button>
                </Link>
                <Link to="/guantes-box">
                  <motion.button
                    className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white py-2 px-4 rounded-lg text-sm font-medium transition-all duration-300 flex items-center border border-white/10 relative overflow-hidden"
                    whileHover={{ scale: 1.05, borderColor: "rgba(239, 68, 68, 0.5)" }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <motion.div
                      className="absolute inset-0 w-full bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"
                      animate={{ x: ["-100%", "100%"] }}
                      transition={{
                        repeat: Infinity,
                        duration: .8,
                        ease: "linear",
                        delay: .85,
                        repeatDelay: 3
                      }}
                    />
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2 z-10 relative"></span>
                    <span className="z-10 relative">{t('home.boxingGlovesBtn')}</span>
                  </motion.button>
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Overview Section */}
      <section className="py-20 bg-gradient-to-r from-gray-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23dc2626' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" dangerouslySetInnerHTML={{ __html: t('home.qualityMaterialsTitle') }}>
              </h2>
              <div className="w-24 h-1 bg-red-600 mx-auto mb-8"></div>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed" dangerouslySetInnerHTML={{ __html: t('home.qualityMaterialsDescription') }}>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center p-8 bg-white rounded-2xl shadow-lg cursor-pointer border border-gray-100"
                whileHover={{
                  y: -5,
                  boxShadow: "0 20px 40px rgba(100, 64, 19, 0.2)",
                  transition: {
                    type: "spring",
                    stiffness: 400,
                    damping: 10
                  },
                }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <div className="text-4xl font-bold text-red-600 mb-2">{t('home.solutionsCard1.title')}</div>
                <div className="text-gray-600 font-medium">{t('home.solutionsCard1.subtitle')}</div>
                <div className="text-sm text-gray-500 mt-2">{t('home.solutionsCard1.description')}</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center p-8 bg-white rounded-2xl shadow-lg cursor-pointer border border-gray-100"
                whileHover={{
                  y: -5,
                  boxShadow: "0 20px 40px rgba(100, 64, 19, 0.2)",
                  transition: {
                    type: "spring",
                    stiffness: 400,
                    damping: 10
                  },
                }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="text-4xl font-bold text-red-600 mb-2">{t('home.solutionsCard2.title')}</div>
                <div className="text-gray-600 font-medium">{t('home.solutionsCard2.subtitle')}</div>
                <div className="text-sm text-gray-500 mt-2">{t('home.solutionsCard2.description')}</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center p-8 bg-white rounded-2xl shadow-lg cursor-pointer border border-gray-100"
                whileHover={{
                  y: -5,
                  boxShadow: "0 20px 40px rgba(100, 64, 19, 0.2)",
                  transition: {
                    type: "spring",
                    stiffness: 400,
                    damping: 10
                  },
                }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <div className="text-4xl font-bold text-red-600 mb-2">{t('home.solutionsCard3.title')}</div>
                <div className="text-gray-600 font-medium">{t('home.solutionsCard3.subtitle')}</div>
                <div className="text-sm text-gray-500 mt-2">{t('home.solutionsCard3.description')}</div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('home.whyPlasaTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('home.whyPlasaSubtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center p-6 bg-white rounded-xl shadow-lg cursor-pointer flex flex-col items-center h-full"
                whileHover={{
                  y: -5,
                  boxShadow: "0 20px 40px rgba(100, 64, 19, 0.2)",
                  transition: {
                    type: "spring",
                    stiffness: 400,
                    damping: 10,
                    duration: 0.6,
                    delay: index * 0.1
                  },
                }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="plasa-red-bg w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">
                  {feature.title}
                </h3>
                {feature.description && <p className="text-gray-600 text-center">{feature.description}</p>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Preview */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('home.bestOfOurLineTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('home.bestOfOurLineSubtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <Link to="/producto/dollaro">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={hoverAnimation}
                transition={transitionConfig}
                viewport={{ once: true }}
                className="relative overflow-hidden rounded-xl shadow-lg cursor-pointer"
              >
                <LazyImage
                  alt={t('home.dollaroAlt')}
                  className="w-full h-64 object-cover"
                  src="/images/home/dollaronappa.webp"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-xl font-semibold mb-1">
                    {t('home.dollaroNappaBtn')}
                  </h3>
                  <p className="text-sm opacity-90">{t('home.viewDetails')}</p>
                </div>
              </motion.div>
            </Link>

            <Link to="/automotriz">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={hoverAnimation}
                transition={{ ...transitionConfig, delay: 0.1 }}
                viewport={{ once: true }}
                className="relative overflow-hidden rounded-xl shadow-lg cursor-pointer"
              >
                <LazyImage
                  alt={t('home.automotiveAlt')}
                  className="w-full h-64 object-cover"
                  src="/images/home/sautomotriz.webp"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-xl font-semibold mb-1">
                    {t('home.automotiveSynthetic')}
                  </h3>
                  <p className="text-sm opacity-90">{t('home.viewDetails')}</p>
                </div>
              </motion.div>
            </Link>

            <Link to="/guantes-box">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={hoverAnimation}
                transition={{ ...transitionConfig, delay: 0.2 }}
                viewport={{ once: true }}
                className="relative overflow-hidden rounded-xl shadow-lg cursor-pointer"
              >
                <LazyImage
                  alt={t('home.boxingGlovesAlt')}
                  className="w-full h-64 object-cover"
                  src="/images/home/vsport.webp"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-xl font-semibold mb-1">{t('home.boxingGlovesBtn')}</h3>
                  <p className="text-sm opacity-90">{t('home.viewDetails')}</p>
                </div>
              </motion.div>
            </Link>
          </div>

          <div className="text-center">
            <Link to="/productos" className="inline-block">
              <button className="modern-button group">
                {t('home.viewOurProductsBtn')}
                <span className="arrow-container opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-bounce-x ml-2 h-5 w-5 inline-block">
                    <circle cx="8" cy="21" r="1"></circle>
                    <circle cx="19" cy="21" r="1"></circle>
                    <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"></path>
                  </svg>
                </span>
                {/* Estilos movidos a index.css */}
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('home.testimonialsTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('home.testimonialsSubtitle')}
            </p>
          </motion.div>

          {/* Modal de Reseña */}
          <AnimatePresence>
            {showReviewForm && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                onClick={() => !submitSuccess && setShowReviewForm(false)}
              >
                <motion.div
                  className="bg-white rounded-xl p-6 w-full max-w-md relative"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={transitionConfig}
                  onClick={(e) => e.stopPropagation()}
                >
                  {!submitSuccess ? (
                    <form onSubmit={handleSubmitReview}>
                      <div className="space-y-4">
                        <div>
                          <label
                            htmlFor="name"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            {t('home.reviewForm.nameOptional')}
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                            placeholder={t('home.reviewForm.yourNamePlaceholder')}
                            value={review.name}
                            onChange={handleInputChange}
                            disabled={isSubmitting}
                          />
                        </div>

                        <div>
                          <label
                            htmlFor="company"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            {t('home.reviewForm.company')} <span class="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="company"
                            name="company"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                            placeholder={t('home.reviewForm.companyPlaceholder')}
                            value={review.company}
                            onChange={handleInputChange}
                            required
                            disabled={isSubmitting}
                          />
                        </div>

                        <div>
                          <label
                            htmlFor="comment"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            {t('home.reviewForm.yourOpinion')} <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            id="comment"
                            name="comment"
                            rows="4"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                            placeholder={t('home.reviewForm.serviceFeedbackPlaceholder')}
                            value={review.comment}
                            onChange={handleInputChange}
                            required
                            disabled={isSubmitting}
                          ></textarea>
                        </div>

                        <div>
                          <p className="block text-sm font-medium text-gray-700 mb-1">
                            <span className="text-red-500">*</span>
                          </p>
                          <StarRating
                            rating={review.rating}
                            onRatingChange={handleRatingChange}
                          />
                        </div>

                        {formError && (
                          <div className="text-red-500 text-sm">
                            {formError}
                          </div>
                        )}

                        <div className="pt-2">
                          <Button
                            type="submit"
                            className="w-full plasa-red-bg hover:bg-red-700"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <div className="flex items-center justify-center">
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {t('home.reviewForm.sending')}
                              </div>
                            ) : (
                              t('home.reviewForm.submitReview')
                            )}
                          </Button>
                        </div>
                      </div>
                    </form>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg
                          className="w-8 h-8 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {t('home.reviewForm.thankYouTitle')}
                      </h3>
                      <p className="text-gray-600 mb-6">
                        {t('home.reviewForm.thankYouMessage')}
                      </p>
                      <Button
                        onClick={() => {
                          setSubmitSuccess(false);
                          setShowReviewForm(false);
                        }}
                        className="plasa-red-bg hover:bg-red-700"
                      >
                        {t('home.reviewForm.close')}
                      </Button>
                    </div>
                  )}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="relative w-full overflow-hidden py-8">
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-red-600" />
                <span className="ml-2">{t('home.loadingReviews')}</span>
              </div>
            ) : error ? (
              <div className="text-center py-8 text-red-600">
                <p>{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                >
                  {t('home.retry')}
                </button>
              </div>
            ) : reviews.length > 0 ? (
              <div className="carousel-container">
                <style>
                  {`
      .carousel-container {
        margin: 0 auto;
        padding: 20px 0;
        max-width: 1200px;
        overflow: hidden;
        position: relative;
      }

      .carousel-track {
        display: flex;
        will-change: transform;
        animation: scrolling 30s linear infinite;
        width: max-content;
      }

      .carousel-group {
        display: flex;
        gap: 20px;
        padding-right: 20px;
        flex-shrink: 0;
      }

      .carousel-group > div {
        flex: 0 0 300px;
        max-width: 300px;
      }

      .carousel-container:hover .carousel-track {
        animation-play-state: paused;
      }

      @keyframes scrolling {
        0% { transform: translateX(0); }
        100% { transform: translateX(calc(-100% / 2)); }
      }

      @media (prefers-reduced-motion: reduce) {
        .carousel-track {
          animation: none;
        }
      }
    `}
                </style>

                {/* Grupo de opiniones (original + duplicado) */}
                <div className="carousel-track">
                  {/* Grupo original */}
                  <div className="carousel-group">
                    {reviews.map((review, index) => (
                      <div
                        key={`original-${review.id || index}`}
                        className="w-[300px] sm:w-[350px] flex-shrink-0"
                      >
                        <ReviewCard review={review} />
                      </div>
                    ))}
                  </div>

                  {/* Grupo duplicado */}
                  <div className="carousel-group" aria-hidden="true">
                    {reviews.map((review, index) => (
                      <div
                        key={`duplicate-${review.id || index}`}
                        className="w-[300px] sm:w-[350px] flex-shrink-0"
                      >
                        <ReviewCard review={review} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <MessageSquare className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">
                  {t('home.noReviewsTitle')}
                </h3>
                <p className="mt-2 text-gray-600">
                  {t('home.noReviewsMessage')}
                </p>
                <button
                  onClick={() => setShowReviewForm(true)}
                  className="modern-button group mt-6"
                >
                  {t('home.writeReviewBtn')}
                  <span className="arrow-container opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-bounce-x ml-2 h-5 w-5 inline-block">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                    </svg>
                  </span>
                </button>
              </div>
            )}
          </div>

          {reviews.length > 0 && (
            <div className="mt-12 text-center">
              <button
                onClick={() => setShowReviewForm(true)}
                className="modern-button group"
              >
                {t('home.writeReviewBtn')}
                <span className="arrow-container opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-bounce-x ml-2 h-5 w-5 inline-block">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                  </svg>
                </span>
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 plasa-gradient text-white relative overflow-hidden">
        {/* Capa de fondo con patrón */}
        <div className="absolute inset-0 w-full h-full">
          <div className="absolute inset-0 hero-pattern"></div>
          {/* Capa semitransparente para mejorar contraste */}
          <div className="absolute inset-0 bg-black/20"></div>
        </div>

        {/* Contenido */}
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold mb-4">
              {t('home.ctaTitle')}
            </h2>
            <p className="text-xl mb-8 opacity-90">
              {t('home.ctaSubtitle')}
            </p>
            <div className="flex justify-center gap-4">
              <Link to="/contacto" className="group">
                <button className="modern-button bg-white text-red-600 hover:text-white">
                  {t('home.contactNowBtn')}
                  <span className="arrow-container opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-bounce-x ml-2 h-5 w-5 inline-block">
                      <path d="M21.2 8.4c.5.38.8.97.8 1.6v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h3.3a2 2 0 0 0 1.4-.6L12 3"></path>
                      <path d="m22 10-10 7-10-7"></path>
                    </svg>
                  </span>
                </button>
              </Link>
              <Link to="/productos" className="group">
                <button className="modern-button bg-white text-red-600 hover:text-white">
                  {t('home.viewOurProductsBtn')}
                  <span className="arrow-container opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-bounce-x ml-2 h-5 w-5 inline-block">
                      <circle cx="8" cy="21" r="1"></circle>
                      <circle cx="19" cy="21" r="1"></circle>
                      <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"></path>
                    </svg>
                  </span>
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
